import { TRANSITION_MAKE_CUSTOM_OFFER, TRANSITION_MAKE_CUSTOM_PRO_OFFER } from './transaction';

// A custom offer lives in the transaction's protected data, written by the
// make-custom-offer transitions (see sendCustomOffer in TransactionPage.duck.js).
// The offer modal, the process steps card and the activity feed all need the
// same view of it, so it is assembled here once.

const OFFER_TRANSITIONS = [TRANSITION_MAKE_CUSTOM_OFFER, TRANSITION_MAKE_CUSTOM_PRO_OFFER];

// True when the transaction's offer transition accepts protectedFileAttachments,
// i.e. its process version runs :action/update-protected-files. Decided from
// the transaction's next process transitions (sdk.processTransitions.query,
// stored as TransactionPage.processTransitions), whose `params` list every
// parameter the transition takes. That is the exact condition under which the
// parameter may be sent, on any marketplace and any process version; a
// transaction started before the process push keeps its old version and
// simply never lists the parameter.
export const offerAttachmentsSupported = nextTransitions =>
  (nextTransitions || []).some(transition => {
    const { name, params } = transition.attributes || {};
    if (!OFFER_TRANSITIONS.includes(name)) {
      return false;
    }
    const { req, opt } = params || {};
    return !!((opt && opt.protectedFileAttachments) || (req && req.protectedFileAttachments));
  });

// The offer's files: the transaction's protected attachments minus anything an
// operator has deleted. Requires the transaction to have been fetched with
// `protectedFileAttachments` and `protectedFileAttachments.file` included.
export const offerAttachments = transaction =>
  (transaction?.protectedFileAttachments || []).filter(
    a => a && !(a.attributes && a.attributes.deleted) && a.file && !a.file.attributes.deleted
  );

/**
 * The transaction's custom offer in the shape CustomOfferModal renders, or
 * null when no offer has been made. The viewer's role picks the total that
 * travels along: customers see what they pay, providers what they earn.
 *
 * Offers made before the title field existed have no `title`, and offers made
 * before the start date existed have no `startDate` -- every consumer renders
 * the delivery date alone in that case.
 */
export const customOfferDataFromTransaction = (transaction, isCustomer) => {
  const { protectedData = {}, payinTotal, payoutTotal, lineItems } = transaction?.attributes || {};
  if (protectedData.customOfferAmount === undefined) {
    return null;
  }
  return {
    amount: protectedData.customOfferAmount,
    title: protectedData.customOfferTitle,
    description: protectedData.customOfferDescription || '',
    startDate: protectedData.customOfferStartDate,
    deliveryDate: protectedData.customOfferDeliveryDate,
    attachments: offerAttachments(transaction),
    lineItems,
    vat: protectedData.vat || null,
    processName: transaction?.attributes?.processName || null,
    ...(isCustomer ? { payinTotal } : { payoutTotal }),
  };
};
